import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';

import LimitInputRow from '@/screens/Merchants/common/LimitInputRow';
import type {AfriexFeatureLimit} from '@/types';
import {AfriexFeatureFrequency} from '@/types';
import {formatSelectOptionItem} from '@/utils/merchant';

interface FrequencyPickersProps {
  onChange: (value: AfriexFeatureLimit) => void;
  value?: AfriexFeatureLimit;
  label?: string;
  dataCy?: string;
}

const typeOptions = [
  {key: '%', value: 'PERCENTAGE' as const},
  {key: '#', value: 'COUNT' as const},
];

const frequencyOptions = Object.values(AfriexFeatureFrequency).map((f) => ({
  key: formatSelectOptionItem(f),
  value: f,
}));

const LimitInput = ({
  label,
  onChange,
  value,
  dataCy,
}: FrequencyPickersProps): ReactElement => {
  const [type, setType] = useState<'PERCENTAGE' | 'COUNT'>('COUNT');
  const [freq, setFreq] = useState<AfriexFeatureFrequency>(
    AfriexFeatureFrequency.Off,
  );

  /* eslint-disable react-hooks/set-state-in-effect -- syncs local type/freq state when the value prop changes */
  useEffect(() => {
    if (value?.type) {
      setType(value.type);
    }
    if (value?.frequency) {
      setFreq(value.frequency);
    }
  }, [value]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const update = (patch: Partial<AfriexFeatureLimit>) => {
    onChange({
      type,
      value: value?.value || 0,
      frequency: freq,
      ...patch,
    });
  };

  return (
    <LimitInputRow
      label={label}
      dataCy={dataCy}
      type={type}
      value={value?.value}
      frequency={freq}
      typeOptions={typeOptions}
      frequencyOptions={frequencyOptions}
      onTypeChange={(t) => {
        setType(t);
        update({type: t});
      }}
      onValueChange={(v) => update({value: v})}
      onFrequencyChange={(f) => {
        setFreq(f);
        update({frequency: f});
      }}
      max={type === 'PERCENTAGE' ? 100 : undefined}
      typeSelectClassName="border-r-0 rounded-r-none bg-transparent min-w-[65px] focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
      frequencySelectClassName="border-l-0 rounded-l-none bg-transparent min-w-[80px] focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
    />
  );
};

export default LimitInput;
